import { Button, Card, CardContent, Skeleton } from "@roberttamas/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";

import { SetupWizard } from "@/components/setup/setup-wizard";
import { getMySetup } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import type { GetMySetupResp } from "@/lib/types";

/**
 * Buyer setup wizard route (/setup/:orderId) — first-time setup only.
 * Orders whose page already exists are redirected to the order dashboard,
 * where every topic is editable. Gated server-side on owning the order
 * and its payment being confirmed.
 */
export function SetupPage() {
  const { t } = useTranslation();
  const { orderId = "" } = useParams<{ orderId: string }>();

  const query = useQuery<GetMySetupResp>({
    queryKey: ["mySetup", orderId],
    queryFn: () => getMySetup({ orderId }),
    enabled: orderId.length > 0,
    retry: false,
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    const message = query.error
      ? toAppError(query.error).message
      : t("errors.generic");
    return (
      <Card>
        <CardContent className="space-y-4 pt-6 text-center">
          <p className="text-sm text-muted-foreground">{message}</p>
          <Button asChild variant="outline">
            <Link to="/my-orders">{t("myOrder.backToMyOrders")}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return <SetupGate orderId={orderId} setup={query.data} />;
}

function SetupGate({
  orderId,
  setup,
}: {
  orderId: string;
  setup: GetMySetupResp;
}) {
  // Captured once on mount: step 1 creating the site invalidates the
  // setup query mid-wizard, and a live check would eject the user to the
  // dashboard right after the first save.
  const [redirect] = useState(setup.site !== null);
  if (redirect) return <Navigate to={`/my-orders/${orderId}`} replace />;
  return <SetupWizard orderId={orderId} setup={setup} />;
}
